import { useState, ChangeEvent, useMemo, useCallback } from 'react';
import { JobCountDetailResponse } from '@doda/api-client/aspida/@types';
import { findJobCount } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import { Item } from '@doda/common/src/constants/queryParams';
import {
  CityDrillDownType,
  PrefectureDrillDownType,
  WardDrillDownType,
} from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/LocationDrillDown/types';
import { LocationDrillDownType } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/LocationDrillDown';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { LOCATION_DRILL_DOWN_TYPES } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/LocationDrillDown/constants';
import { AreaData, InitialLocationDataType } from '../../types';

const getPrefectureDrillDownList = (
  pr: Item | undefined,
  locationDrillDown:
    | PrefectureDrillDownType
    | CityDrillDownType
    | WardDrillDownType
    | null
    | undefined
) => {
  if (locationDrillDown?.type === 'prefecture') {
    return locationDrillDown.prefectures
      .filter(({ isChecked }) => isChecked)
      .map(({ id }) => id);
  }

  if (
    pr &&
    locationDrillDown?.type === 'city' &&
    locationDrillDown.prefecture.isChecked
  ) {
    return [locationDrillDown.prefecture.id];
  }

  return [];
};

const getCityDrillDownList = (
  ci: Item | undefined,
  locationDrillDown:
    | PrefectureDrillDownType
    | CityDrillDownType
    | WardDrillDownType
    | null
    | undefined
) => {
  if (locationDrillDown?.type === 'city') {
    return locationDrillDown.prefecture.cities
      .filter(({ isChecked }) => isChecked)
      .map(({ id }) => id);
  }

  if (
    ci &&
    locationDrillDown?.type === 'ward' &&
    locationDrillDown.prefecture.city.isChecked
  ) {
    return [locationDrillDown.prefecture.city.id];
  }

  return [];
};

interface ArgumentType {
  initialLocationData: InitialLocationDataType;
  initialLocationDrillDown?: LocationDrillDownType | null;
}

/**
 * 勤務地に関するhooks
 * @param param.initialLocationData 整形された勤務地マスタ
 * @param param.initialLocationDrillDown ドリルダウン
 * @returns 表示用配列と更新処理
 */
export const useLocation = ({
  initialLocationData,
  initialLocationDrillDown,
}: ArgumentType) => {
  const { preBtn } = useAppSelector(appQueryParamsSelector);
  const isDrillDown = preBtn === '1';
  const [areaList, setAreaList] = useState(initialLocationData.areaData ?? []);
  const [prefectureList, setPrefectureList] = useState(
    initialLocationData.prefectureData ?? []
  );
  const [cityList, setCityList] = useState(initialLocationData.cityData ?? []);
  const [wardList, setWardList] = useState(initialLocationData.wardData ?? []);

  // 表示用配列
  const locationDisplayList: AreaData[] = areaList.map((area) => ({
    ...area,
    prefectureList: prefectureList
      .filter(({ parentId }) => parentId === area.id)
      .map((prefecture) => ({
        ...prefecture,
        cityList: cityList
          .filter(({ parentId }) => parentId === prefecture.id)
          .map((city) => ({
            ...city,
            wardList: wardList.filter(({ parentId }) => parentId === city.id),
          })),
      })),
  }));

  // 選択中の地域のid配列
  const selectedAreaIdList = useMemo(() => {
    return areaList.filter(({ isChecked }) => isChecked).map(({ id }) => id);
  }, [areaList]);

  // 選択中の都道府県のid配列
  const selectedPrefectureIdList = useMemo(() => {
    return prefectureList
      .filter(({ isChecked }) => isChecked)
      .map(({ id }) => id);
  }, [prefectureList]);

  // 選択中の市区町村のid配列
  const selectedCityIdList = useMemo(() => {
    return cityList.filter(({ isChecked }) => isChecked).map(({ id }) => id);
  }, [cityList]);

  // 選択中の政令指定都市のid配列
  const selectedWardIdList = useMemo(() => {
    return wardList.filter(({ isChecked }) => isChecked).map(({ id }) => id);
  }, [wardList]);

  // 地域のチェックボックス更新処理
  const updateIsCheckedAreaList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = areaList.map((area) => {
        if (area.id !== selectedId) return area;

        return {
          ...area,
          isChecked: event.target.checked,
        };
      });

      setAreaList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [areaList]
  );

  // 都道府県のチェックボックス更新処理
  const updateIsCheckedPrefectureList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = prefectureList.map((prefecture) => {
        if (prefecture.id !== selectedId) return prefecture;

        return {
          ...prefecture,
          isChecked: event.target.checked,
        };
      });

      setPrefectureList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [prefectureList]
  );

  // 市区町村のチェックボックス更新処理
  const updateIsCheckedCityList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = cityList.map((city) => {
        if (city.id !== selectedId) return city;

        return {
          ...city,
          isChecked: event.target.checked,
        };
      });

      setCityList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [cityList]
  );

  // 政令指定都市のチェックボックス更新処理
  const updateIsCheckedWardList = useCallback(
    (event: ChangeEvent<HTMLInputElement>, selectedId: string) => {
      const checkedList = wardList.map((ward) => {
        if (ward.id !== selectedId) return ward;

        return {
          ...ward,
          isChecked: event.target.checked,
        };
      });

      setWardList(checkedList);

      return checkedList
        .filter(({ isChecked }) => isChecked)
        .map(({ id }) => id);
    },
    [wardList]
  );

  // 地域の求人件数更新処理
  const updateAreaListJobCount = useCallback(
    (areaIdList: JobCountDetailResponse[] | undefined) => {
      setAreaList((pre) =>
        pre.map((area) => {
          // チェックが入っていない場合は更新しない
          if (!area.isChecked) return area;

          return {
            ...area,
            jobCount: findJobCount(areaIdList, area.id),
          };
        })
      );
    },
    []
  );

  // 都道府県の求人件数更新処理
  const updatePrefectureListJobCount = useCallback(
    (prefectureIdList: JobCountDetailResponse[] | undefined) => {
      setPrefectureList((pre) =>
        pre.map((prefecture) => {
          // チェックが入っていない場合は更新しない
          if (!prefecture.isChecked) return prefecture;

          return {
            ...prefecture,
            jobCount: findJobCount(prefectureIdList, prefecture.id),
          };
        })
      );
    },
    []
  );

  // 市区町村の求人件数更新処理
  const updateCityListJobCount = useCallback(
    (cityIdList: JobCountDetailResponse[] | undefined) => {
      setCityList((pre) =>
        pre.map((city) => {
          // チェックが入っていない場合は更新しない
          if (!city.isChecked) return city;

          return {
            ...city,
            jobCount: findJobCount(cityIdList, city.id),
          };
        })
      );
    },
    []
  );

  // 政令指定都市の求人件数更新処理
  const updateWardListJobCount = useCallback(
    (wardIdList: JobCountDetailResponse[] | undefined) => {
      setWardList((pre) =>
        pre.map((ward) => {
          // チェックが入っていない場合は更新しない
          if (!ward.isChecked) return ward;

          return {
            ...ward,
            jobCount: findJobCount(wardIdList, ward.id),
          };
        })
      );
    },
    []
  );

  const { pr, ci } = useAppSelector(appQueryParamsSelector);

  const [locationDrillDown, setLocationDrillDown] = useState(
    initialLocationDrillDown
  );

  const selectedAreaDrillDownList =
    locationDrillDown?.type === 'prefecture' && locationDrillDown.isChecked
      ? [locationDrillDown.id]
      : [];

  // 選択されている都道府県のidリスト
  const selectedPrefectureDrillDownList = getPrefectureDrillDownList(
    pr,
    locationDrillDown
  );

  // 選択されている市区町村のドリルダウンのidリスト
  const selectedCityDrillDownList = getCityDrillDownList(ci, locationDrillDown);

  // 選択されている政令指定都市のドリルダウンのidリスト
  const selectedWardDrillDownList =
    locationDrillDown?.type === LOCATION_DRILL_DOWN_TYPES.WARD
      ? locationDrillDown.prefecture.city.ward
          .filter(({ isChecked }) => isChecked)
          .map(({ id }) => id)
      : [];

  const updateLocationDrillDownIsChecked = useCallback(
    (
      event: ChangeEvent<HTMLInputElement>,
      selectedId: string
    ):
      | {
          areaId?: string[];
          prefectureId?: string[];
          cityId?: string[];
          wardId?: string[];
        }
      | undefined => {
      if (!locationDrillDown) return undefined;

      switch (locationDrillDown.type) {
        case 'prefecture': {
          const checkedList = locationDrillDown.prefectures.map(
            (prefecture) => {
              if (selectedId !== prefecture.id) return prefecture;

              return {
                ...prefecture,
                isChecked: event.target.checked,
                isBold: event.target.checked,
              };
            }
          );

          // 都道府県にチェックが入っている場合は地域のisCheckedをfalseにする
          const updatedLocationDrillDown = {
            ...locationDrillDown,
            isChecked: checkedList.every((item) => !item.isChecked),
            prefectures: checkedList,
          };
          setLocationDrillDown(updatedLocationDrillDown);

          return {
            areaId: updatedLocationDrillDown.isChecked
              ? [updatedLocationDrillDown.id]
              : [],
            prefectureId: checkedList
              .filter(({ isChecked }) => isChecked)
              .map(({ id }) => id),
          };
        }
        case 'city': {
          const checkedList = locationDrillDown.prefecture.cities.map(
            (city) => {
              if (city.id !== selectedId) return city;
              return {
                ...city,
                isChecked: event.target.checked,
                isBold: event.target.checked,
              };
            }
          );
          /**
           * prがクエリに存在する場合は
           * 市区町村のチェック状態を元に都道府県のisCheckedを切り替える
           */
          const updatedLocationDrillDown = {
            ...locationDrillDown,
            prefecture: {
              ...locationDrillDown.prefecture,
              isChecked: pr
                ? checkedList.every((item) => !item.isChecked)
                : false,
              cities: checkedList,
            },
          };
          setLocationDrillDown(updatedLocationDrillDown);

          return {
            prefectureId: updatedLocationDrillDown.prefecture.isChecked
              ? [updatedLocationDrillDown.prefecture.id]
              : [],
            cityId: checkedList
              .filter(({ isChecked }) => isChecked)
              .map(({ id }) => id),
          };
        }
        case 'ward': {
          const checkedList = locationDrillDown.prefecture.city.ward.map(
            (ward) => {
              if (ward.id !== selectedId) return ward;

              return {
                ...ward,
                isChecked: event.target.checked,
                isBold: event.target.checked,
              };
            }
          );

          /**
           * ciがクエリに存在する場合は
           * 政令指定都市のチェック状態を元に市区町村のisCheckedを切り替える
           */
          const updatedLocationDrillDown = {
            ...locationDrillDown,
            prefecture: {
              ...locationDrillDown.prefecture,
              city: {
                ...locationDrillDown.prefecture.city,
                isChecked: ci
                  ? checkedList.every((item) => !item.isChecked)
                  : false,
                ward: checkedList,
              },
            },
          };

          setLocationDrillDown(updatedLocationDrillDown);

          return {
            cityId: updatedLocationDrillDown.prefecture.city.isChecked
              ? [updatedLocationDrillDown.prefecture.city.id]
              : [],
            wardId: checkedList
              .filter(({ isChecked }) => isChecked)
              .map(({ id }) => id),
          };
        }
        default:
          return undefined;
      }
    },
    [ci, locationDrillDown, pr]
  );

  // 求人件数の更新処理
  const updateLocationJobCount = (
    area: JobCountDetailResponse[] | undefined,
    prefecture: JobCountDetailResponse[] | undefined,
    city: JobCountDetailResponse[] | undefined,
    ward: JobCountDetailResponse[] | undefined
  ) => {
    updateAreaListJobCount(area);
    updatePrefectureListJobCount(prefecture);
    updateCityListJobCount(city);
    updateWardListJobCount(ward);
  };

  return {
    locationDisplayList,
    selectedAreaIdList: isDrillDown
      ? selectedAreaDrillDownList
      : selectedAreaIdList,
    selectedPrefectureIdList: isDrillDown
      ? selectedPrefectureDrillDownList
      : selectedPrefectureIdList,
    selectedCityIdList: isDrillDown
      ? selectedCityDrillDownList
      : selectedCityIdList,
    selectedWardIdList: isDrillDown
      ? selectedWardDrillDownList
      : selectedWardIdList,
    // ドリルダウン
    locationDrillDown,
    updateIsCheckedAreaList,
    updateIsCheckedPrefectureList,
    updateIsCheckedCityList,
    updateIsCheckedWardList,
    updateLocationDrillDownIsChecked,
    updateLocationJobCount,
  };
};
